import { Global } from '@emotion/react';
import styled from '@emotion/styled';
import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { ChatInterface } from './components/chat/ChatInterface';
import { SoulWelcomeModal } from './components/chat/SoulWelcomeModal';
import { useChat } from './hooks/useChat';
import { useUserProfile } from './hooks/useUserProfile';
import { useDailyRoutines } from './hooks/useDailyRoutines';
import { createGlobalStyles } from './styles/global';
import { theme } from './styles/theme';
import { LanguageProvider, useTranslation } from './i18n';
import { ThemeProvider, useTheme } from './hooks/useTheme';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initAnalytics, setUser, trackSoulPageOpened, trackSoulChatStart } from './services/analytics.service';

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  color: ${theme.colors.neutral.white};
  font-family: ${theme.typography.fontFamily.primary};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${theme.colors.neutral.dark};
  border-top-color: ${theme.colors.primary.main};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  padding: 24px;
  text-align: center;
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.primary};
  gap: 16px;
  background: var(--color-bg);
`;

const ErrorSoulLogo = styled.img`
  width: 72px;
  height: 72px;
  margin-bottom: 8px;
  opacity: 0.9;
`;

const ErrorTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: var(--color-text);
`;

const ErrorMessage = styled.p`
  font-size: 14px;
  line-height: 1.5;
  max-width: 320px;
  margin: 0;
  color: var(--color-text-secondary);
`;

const ErrorRetryButton = styled.button`
  margin-top: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:active {
    transform: scale(0.97);
  }
`;

function AppContent() {
  const { t, tArray, lang } = useTranslation();
  const { mode, theme: activeTheme } = useTheme();

  // Sync theme mode to body data attribute (for CSS selectors like bg-image)
  useEffect(() => {
    document.body.setAttribute('data-theme', mode);
  }, [mode]);

  // Init Amplitude once on mount. All track() calls are no-ops until
  // setUser() is invoked below (Amplitude requires a user_id on every event).
  useEffect(() => {
    initAnalytics();
  }, []);

  // Extract token from query params
  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || undefined;
  }, []);

  // Fetch user profile
  const { userInfo, isLoading: userProfileLoading, error: userProfileError } = useUserProfile({
    apiUrl: import.meta.env.VITE_API_URL || '/api',
    token,
  });

  // Hard-gate Soul usage: without a valid userId we can't personalise the
  // experience, send analytics, or call any backend endpoint safely. Surface
  // a blocking error screen so the user can't send messages in this state.
  const hasValidProfile = !!userInfo?.userId;
  const profileLoadFailed = !userProfileLoading && !hasValidProfile;

  // Attach userId + B2B company to all subsequent analytics events.
  // Soul Page Opened is fired here (instead of on mount) so we never emit
  // events without a user_id — Amplitude requires one on every call.
  useEffect(() => {
    if (userInfo?.userId) {
      setUser(userInfo.userId, { companyName: userInfo.companyName ?? null });
      trackSoulPageOpened();
    }
  }, [userInfo?.userId, userInfo?.companyName]);

  // Safety-net retry: some sessions finish user-profile fetch late and the
  // useEffect above races with Amplitude init. Re-attempt a few times in the
  // first ~10s — the helper is idempotent (fires once max).
  useEffect(() => {
    const timers = [3000, 6000, 10000].map((ms) =>
      window.setTimeout(() => trackSoulPageOpened(), ms)
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, []);

  const handleLocationRequest = async (): Promise<{ latitude: number; longitude: number } | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.warn('Geolocation not supported');
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.warn('Location permission denied:', error);
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 300000, // 5 minutes cache
        }
      );
    });
  };

  const { sessionId, messages, isStreaming, isInitializing, contentRecommendations, activeSkillId, skillSuggestions, agentOverride, setAgentOverride, isConversationEnded, mhWrapUp, setMhWrapUp, sendMessage, addAssistantMessage, clearMessages, loadMessages, startSkillSession, exitSkillMode } = useChat({
    apiUrl: import.meta.env.VITE_API_URL || '/api',
    userId: userInfo?.userId,
    token,
    onLocationRequest: handleLocationRequest,
  });

  const { weeklyRoutines, categorizedRoutines, isLoading: routinesLoading, isGenerating: routinesGenerating, completeTask } = useDailyRoutines({
    userId: userInfo?.userId,
    token,
    apiUrl: import.meta.env.VITE_API_URL || '/api',
    enabled: !!userInfo?.userId && !!token,
    lang,
  });

  const skillKeys: Array<{ id: string; key: string; icon: string; color: string; colorEnd: string; coverImage: string }> = [
    { id: 'stress-coach', key: 'stressCoach', icon: '🧘', color: '#6B4EAA', colorEnd: '#9B6DD7', coverImage: '/img/skills-covers/Stress Coach.jpg' },
    { id: 'sleep-optimizer', key: 'sleepOptimizer', icon: '🌙', color: '#2D3A8C', colorEnd: '#4C6EF5', coverImage: '/img/skills-covers/Sleep Optimizer.jpg' },
    { id: 'work-life-balance', key: 'workLifeBalance', icon: '⚖️', color: '#1B7A4A', colorEnd: '#38D9A9', coverImage: '/img/skills-covers/Work-Life Balance_.jpg' },
    { id: 'focus-mode', key: 'focusMode', icon: '🎯', color: '#C2255C', colorEnd: '#F06595', coverImage: '/img/skills-covers/Focus Mode.jpg' },
    { id: 'career-coach', key: 'careerCoach', icon: '🚀', color: '#E67700', colorEnd: '#FFA94D', coverImage: '/img/skills-covers/Career Coach.jpg' },
    { id: 'habit-builder', key: 'habitBuilder', icon: '🔄', color: '#D6336C', colorEnd: '#F783AC', coverImage: '/img/skills-covers/Habit Builder.jpg' },
    { id: 'desk-wellness', key: 'deskWellness', icon: '🪑', color: '#0B7285', colorEnd: '#22B8CF', coverImage: '/img/skills-covers/Desk Wellness.jpg' },
    { id: 'financial-wellbeing', key: 'financialWellbeing', icon: '💰', color: '#2B8A3E', colorEnd: '#51CF66', coverImage: '/img/skills-covers/Financial Wellbeing.jpg' },
    { id: 'priority-coach', key: 'priorityCoach', icon: '✅', color: '#5F3DC4', colorEnd: '#9775FA', coverImage: '/img/skills-covers/Priority Coach.jpg' },
    { id: 'motivation-coach', key: 'motivationCoach', icon: '🔥', color: '#862E9C', colorEnd: '#CC5DE8', coverImage: '/img/skills-covers/Motivation Coach.jpg' },
    { id: 'digital-balance', key: 'digitalBalance', icon: '📱', color: '#364FC7', colorEnd: '#748FFC', coverImage: '/img/skills-covers/Digital Balance.jpg' },
    { id: 'creativity-coach', key: 'creativityCoach', icon: '🎨', color: '#C92A2A', colorEnd: '#FF6B6B', coverImage: '/img/skills-covers/Creativity Coach.jpg' },
    { id: 'soul-reflective-approach', key: 'soulReflective', icon: '🪷', color: '#5C4D7D', colorEnd: '#9B8EC4', coverImage: '/img/skills-covers/Reflective Approach.jpg' },
    { id: 'soul-solution-oriented', key: 'soulSolutionOriented', icon: '🌱', color: '#2D6A4F', colorEnd: '#52B788', coverImage: '/img/skills-covers/Solution Oriented.jpg' },
  ];

  const availableSkills = skillKeys.map(({ id, key, icon, color, colorEnd, coverImage }) => ({
    id,
    name: t(`skills.${key}.name`),
    description: t(`skills.${key}.description`),
    icon,
    color,
    colorEnd,
    coverImage,
    welcomeMessage: t(`skills.${key}.welcomeMessage`),
    initialSuggestions: tArray(`skills.${key}.suggestions`),
  }));

  const noInitialSuggestionsSkills = ['soul-reflective-approach', 'soul-solution-oriented'];

  const handleSkillSelect = (skillId: string) => {
    const skill = availableSkills.find((s) => s.id === skillId);
    const suggestions = noInitialSuggestionsSkills.includes(skillId) ? [] : skill?.initialSuggestions;
    startSkillSession(skillId, skill?.welcomeMessage, suggestions);
  };

  const handleExitSkill = () => {
    exitSkillMode();
  };

  const handleSendMessage = (content: string, imageUrl?: string, overrideAgent?: string) => {
    sendMessage(content, imageUrl, overrideAgent);
  };

  const handleMessageAction = (messageId: string | number, action: 'like' | 'dislike') => {
    fetch(`/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        messageId,
        action,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          console.error(`Error sending feedback: server responded with ${response.status}`);
        }
      })
      .catch((error) => {
        console.error('Error sending feedback:', error);
      });
  };

  const handleSuggestionSelected = (suggestion: string) => {
    // Add Soul's question as an assistant message
    addAssistantMessage(suggestion);
    // Chat is already visible, no need to toggle
  };

  // === Post-conversation feedback popup (once per day) ===
  const [feedbackPopup, setFeedbackPopup] = useState<{ sessionId: string } | null>(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  // === First-time Soul welcome popup (once ever) ===
  // Primary gate is a localStorage flag. DB fallback: if the user already has
  // chat history (returning user whose storage was cleared / new device), the
  // GET /sessions endpoint returns sessions that have messages, so we suppress
  // the popup. Empty (eagerly-created) sessions are excluded server-side, so a
  // genuinely new user reads as having no history.
  const [showWelcome, setShowWelcome] = useState(false);
  const welcomeCheckedRef = useRef(false);
  useEffect(() => {
    const uid = userInfo?.userId;
    if (!uid || isInitializing || welcomeCheckedRef.current) return;
    const WELCOME_KEY = 'soul-welcome-intro-seen';
    if (localStorage.getItem(WELCOME_KEY)) {
      welcomeCheckedRef.current = true;
      return;
    }
    welcomeCheckedRef.current = true;
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    fetch(`${apiUrl}/chat/sessions/${uid}?limit=1`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('sessions check failed'))))
      .then((data) => {
        const hasHistory = (data?.data?.total ?? 0) > 0;
        // Mark as seen either way so the popup (and this check) never repeats.
        localStorage.setItem(WELCOME_KEY, '1');
        if (!hasHistory) setShowWelcome(true);
      })
      .catch(() => {
        // Transient failure — allow a retry on the next app load.
        welcomeCheckedRef.current = false;
      });
  }, [userInfo?.userId, isInitializing]);

  const shouldShowFeedback = useCallback(() => {
    // Only show if user had a real conversation (3+ messages)
    const userMsgCount = messages.filter(m => m.role === 'user').length;
    if (userMsgCount < 3) return false;
    // If mhWrapUp already shown rating inline, count as shown today
    if (mhWrapUp) {
      localStorage.setItem('soul-feedback-date', new Date().toISOString().slice(0, 10));
      return false;
    }
    // Check localStorage for today
    const lastShown = localStorage.getItem('soul-feedback-date');
    const today = new Date().toISOString().slice(0, 10);
    return lastShown !== today;
  }, [messages, mhWrapUp]);

  const handleBack = () => {
    if (activeSkillId) {
      exitSkillMode();
      return;
    }
    // If there are messages, check if we should show feedback popup
    if (messages.length > 0) {
      if (shouldShowFeedback()) {
        setFeedbackPopup({ sessionId });
        localStorage.setItem('soul-feedback-date', new Date().toISOString().slice(0, 10));
      }
      clearMessages();
    }
  };

  // Fire "Soul Chat Start" once the (empty) chat screen is actually shown —
  // init finished, profile valid, and no messages loaded yet (fresh chat).
  // Web-only + idempotent inside the helper. userId is already set by the
  // effect above (declared earlier → runs first), so the user is identified.
  useEffect(() => {
    if (isInitializing || !hasValidProfile) return; // chat screen not shown yet
    if (messages.length > 0) return;                // not an empty/fresh chat
    trackSoulChatStart();
  }, [isInitializing, hasValidProfile, messages.length]);

  if (isInitializing) {
    return (
      <>
        <Global styles={createGlobalStyles(activeTheme)} />
        <LoadingContainer>
          <Spinner />
          <p style={{ marginTop: '20px' }}>{t('chat.initializing')}</p>
        </LoadingContainer>
      </>
    );
  }

  // Hard gate — block access when the user profile endpoint returned nothing
  // (missing token, expired session, backend error). The rest of the app
  // relies on a valid user id, so we short-circuit rendering here.
  if (profileLoadFailed) {
    return (
      <>
        <Global styles={createGlobalStyles(activeTheme)} />
        <ErrorContainer>
          <ErrorSoulLogo src="/img/soul-logo-gray.svg" alt="Soul" />
          <ErrorTitle>
            {lang === 'tr' ? 'Bir sorun oluştu' : 'Something went wrong'}
          </ErrorTitle>
          <ErrorMessage>
            {lang === 'tr'
              ? 'Soul kullanıcı bilgilerinizi alamıyor. Lütfen uygulamayı yeniden açmayı deneyin. Sorun devam ederse destek ekibimize yazabilirsiniz.'
              : "Soul can't load your user information right now. Please try reopening the app. If the issue persists, reach out to our support team."}
          </ErrorMessage>
          <ErrorRetryButton onClick={() => window.location.reload()}>
            {lang === 'tr' ? 'Tekrar Dene' : 'Try Again'}
          </ErrorRetryButton>
          {userProfileError ? (
            <ErrorMessage style={{ fontSize: 11, opacity: 0.6 }}>
              {userProfileError.message}
            </ErrorMessage>
          ) : null}
        </ErrorContainer>
      </>
    );
  }

  return (
    <>
      <Global styles={createGlobalStyles(activeTheme)} />
      <ChatInterface
        messages={messages}
        onSendMessage={handleSendMessage}
        isLoading={isStreaming}
        onMessageAction={handleMessageAction}
        onSuggestionSelected={handleSuggestionSelected}
        onBack={handleBack}
        onSessionLoad={loadMessages}
        sessionId={sessionId}
        contentRecommendations={contentRecommendations}
        userInfo={userInfo}
        userProfileLoading={userProfileLoading}
        token={token}
        weeklyRoutines={weeklyRoutines}
        categorizedRoutines={categorizedRoutines}
        routinesLoading={routinesLoading}
        routinesGenerating={routinesGenerating}
        onCompleteTask={completeTask}
        availableSkills={availableSkills}
        onSkillSelect={handleSkillSelect}
        activeSkillId={activeSkillId}
        onExitSkill={handleExitSkill}
        skillSuggestions={skillSuggestions}
        selectedAgent={agentOverride}
        onAgentChange={setAgentOverride}
        isConversationEnded={isConversationEnded}
        mhWrapUp={mhWrapUp}
        onMhWrapUpAction={(action) => {
          if (action === 'continue') {
            setMhWrapUp(false);
          }
        }}
        onMhEndConversation={async () => {
          // Close session on backend
          if (sessionId) {
            try {
              await fetch(`${(import.meta.env.VITE_API_URL || '/api')}/chat/sessions/${sessionId}/close`, {
                method: 'POST',
                credentials: 'include',
              });
            } catch (e) {
              console.error('Error closing session:', e);
            }
          }
        }}
        clearMessages={clearMessages}
        addAssistantMessage={addAssistantMessage}
      />

      {/* First-time Soul welcome popup (once ever, with DB-history fallback) */}
      <SoulWelcomeModal isOpen={showWelcome} onClose={() => setShowWelcome(false)} />

      {/* Post-conversation feedback popup (once per day) */}
      {feedbackPopup && (
        <FeedbackOverlay onClick={() => { setFeedbackPopup(null); setFeedbackRating(0); setFeedbackSubmitted(false); }}>
          <FeedbackBox onClick={(e) => e.stopPropagation()}>
            {feedbackSubmitted ? (
              <>
                <FeedbackTitle>{lang === 'tr' ? 'Değerlendirmen için teşekkürler!' : 'Thanks for your feedback!'}</FeedbackTitle>
                <FeedbackClose onClick={() => { setFeedbackPopup(null); setFeedbackRating(0); setFeedbackSubmitted(false); }}>
                  {lang === 'tr' ? 'Kapat' : 'Close'}
                </FeedbackClose>
              </>
            ) : (
              <>
                <FeedbackTitle>{lang === 'tr' ? 'Son konuşmanı nasıl değerlendirirsin?' : 'How was your last conversation?'}</FeedbackTitle>
                <FeedbackStars>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FeedbackStar key={star} $active={star <= feedbackRating} onClick={() => setFeedbackRating(star)}>
                      {star <= feedbackRating ? '★' : '☆'}
                    </FeedbackStar>
                  ))}
                </FeedbackStars>
                <FeedbackActions>
                  <FeedbackSkip onClick={() => { setFeedbackPopup(null); setFeedbackRating(0); }}>
                    {lang === 'tr' ? 'Geç' : 'Skip'}
                  </FeedbackSkip>
                  <FeedbackSubmit
                    disabled={feedbackRating < 1}
                    onClick={async () => {
                      if (feedbackRating < 1) return;
                      try {
                        const apiUrl = import.meta.env.VITE_API_URL || '/api';
                        const response = await fetch(`${apiUrl}/chat/sessions/${feedbackPopup.sessionId}/rate`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ rating: feedbackRating }),
                        });
                        if (!response.ok) {
                          console.error(`Error saving rating: server responded with ${response.status}`);
                        }
                      } catch (e) {
                        console.error('Error saving rating:', e);
                      }
                      setFeedbackSubmitted(true);
                    }}
                  >
                    {lang === 'tr' ? 'Gönder' : 'Submit'}
                  </FeedbackSubmit>
                </FeedbackActions>
              </>
            )}
          </FeedbackBox>
        </FeedbackOverlay>
      )}
    </>
  );
}

// Feedback popup styled components
const FeedbackOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const FeedbackBox = styled.div`
  background: var(--color-card-bg);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  padding: 28px 24px;
  max-width: 320px;
  width: 100%;
  text-align: center;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
`;

const FeedbackTitle = styled.p`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 20px;
`;

const FeedbackStars = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const FeedbackStar = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 36px;
  color: ${({ $active }) => $active ? '#FFB800' : 'var(--color-border)'};
  transition: all 0.15s ease;
  line-height: 1;

  &:active {
    transform: scale(1.2);
  }
`;

const FeedbackActions = styled.div`
  display: flex;
  gap: 10px;
`;

const FeedbackSkip = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text-secondary);

  &:active { transform: scale(0.97); }
`;

const FeedbackSubmit = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: var(--color-primary);
  color: #FFFFFF;
  opacity: ${({ disabled }) => disabled ? 0.4 : 1};

  &:active:not(:disabled) { transform: scale(0.97); }
`;

const FeedbackClose = styled.button`
  padding: 12px 32px;
  border-radius: 12px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: var(--color-primary);
  color: #FFFFFF;

  &:active { transform: scale(0.97); }
`;

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
